// Native push-notification integration (Android, Capacitor).
//
// EVERYTHING here is a NO-OP in a normal browser. Every entry point first
// checks Capacitor.isNativePlatform(); a web visitor to whatsapp.magic-lap.com
// runs none of this code path and is completely unaffected. The plugin's JS is
// bundled into the site, but it only proxies to a real native implementation
// when running inside the APK shell (server.url loads the live site into the
// Capacitor WebView, where window.Capacitor.Plugins.PushNotifications exists).
//
// Flow (native only):
//   1. request POST_NOTIFICATIONS permission; on grant → PushNotifications.register()
//   2. 'registration'  → we receive the FCM token → POST /api/push/register
//      (also fires again on token refresh → we re-register the new token)
//   3. 'pushNotificationActionPerformed' (tap) → deep-link via the data payload
//      the worker sends: { tenantId, ticketId | phone }
//   4. permission denied → nothing registers; the app works normally, no crash
//   5. logout → unregisterCurrentToken() removes this device's token
//
// The FCM data payload values are strings (FCM requirement); see the worker's
// buildContent() (dashboard/src/services/pushWorker.ts).

import { Capacitor } from '@capacitor/core';
import { registerPushToken, unregisterPushToken } from '../api/push';

/** Where a notification tap should take the user. */
export type PushDeepLink =
  | { kind: 'ticket'; ticketId: string }
  | { kind: 'conversation'; phone: string };

export type DeepLinkHandler = (target: PushDeepLink) => void;

/** True only inside the native (APK) shell. Exported so callers can guard too. */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

// The most recently registered FCM token, kept so logout can unregister it.
let currentToken: string | null = null;

/** Map the worker's data payload to a deep-link target. ticketId wins over phone. */
function toDeepLink(data: Record<string, unknown> | undefined): PushDeepLink | null {
  if (!data) return null;
  const ticketId = typeof data['ticketId'] === 'string' ? data['ticketId'] : null;
  if (ticketId) return { kind: 'ticket', ticketId };
  const phone = typeof data['phone'] === 'string' ? data['phone'] : null;
  if (phone) return { kind: 'conversation', phone };
  return null;
}

/**
 * Initialize native push. No-op (returns a no-op cleanup) in a browser.
 * Idempotent-ish: removes any prior listeners before re-adding, so mounting the
 * hook twice (StrictMode / re-login) doesn't stack duplicate handlers.
 */
export async function initPush(onDeepLink: DeepLinkHandler): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) return () => {};

  // Import lazily so the plugin is only touched on native — keeps the web path
  // from ever exercising the native bridge.
  const { PushNotifications } = await import('@capacitor/push-notifications');

  await PushNotifications.removeAllListeners();

  // Token arrives here on first register AND on every refresh → (re-)register it.
  await PushNotifications.addListener('registration', (tok: { value: string }) => {
    currentToken = tok.value;
    // Non-fatal: a failed registration just means no push until next try.
    void registerPushToken(tok.value).catch(() => {});
  });

  await PushNotifications.addListener('registrationError', () => {});

  // Tap on a delivered notification (app in background/closed) → deep-link.
  await PushNotifications.addListener('pushNotificationActionPerformed', (action: { notification: { data: Record<string, unknown> } }) => {
    const target = toDeepLink(action.notification?.data);
    if (target) onDeepLink(target);
  });

  // Permission → register. checkPermissions first so we don't re-prompt.
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'granted') {
      await PushNotifications.register();
    }
  } catch {
    // Never let push setup crash the app.
  }

  return () => {
    void PushNotifications.removeAllListeners();
  };
}

/**
 * Remove this device's token on logout. No-op in a browser or when no token was
 * ever registered. Best-effort: must run BEFORE the session is torn down (the
 * endpoint requires auth), and must never throw into the logout flow.
 */
export async function unregisterCurrentToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const token = currentToken;
  if (!token) return;
  currentToken = null;
  try {
    await unregisterPushToken(token);
  } catch {}
}
